import { useCallback } from 'react'

import { feeDetailsSection } from '@services/sections/lib/billing'
import { ComplexTable, TableCard } from '@shared/components/tables'
import { FeeSchedule, useFeeReportsScheduleQuery } from '@shared/graphql'
import { useRouter } from 'next/router'
import useTranslation from 'next-translate/useTranslation'

import { useDailyFeesColumns } from './DailyFeesTable.Columns'

import type { Row } from '@circlefin/components/lib/AdvancedTable'
import type {
  FeeReport,
  GrossRedemptionFeePayment,
  NetRedemptionFeePayment,
} from '@shared/graphql'

const { Header, Body } = TableCard

export interface DailyFeesTableProps {
  /**
   * Payment.
   */
  payment: GrossRedemptionFeePayment | NetRedemptionFeePayment
  /**
   * Controls the amount of lines in the table for each paginated page.
   */
  pageSize?: number
}

export const DailyFeesTable: React.FC<DailyFeesTableProps> = ({
  payment,
  pageSize = 10,
}) => {
  const { t } = useTranslation('payments')
  const router = useRouter()

  const { data, loading, error, refetch } = useFeeReportsScheduleQuery({
    variables: {
      id: payment.id,
      schedule: FeeSchedule.MONTHLY,
    },
  })

  const onRowClick = useCallback(
    ({ original }: Row<FeeReport>) => {
      void router.push({
        pathname: feeDetailsSection.route,
        query: {
          id: original.id,
        },
      })
    },
    [router],
  )

  const columns = useDailyFeesColumns()

  return (
    <TableCard paddingVariant="sm">
      <Header>
        <span className="pl-4 type-h-title-md">
          {t('redemptionFeeDetail.table.title')}
        </span>
      </Header>
      <Body>
        <ComplexTable<FeeReport>
          columns={columns}
          data={data?.feeReportsSchedule ?? []}
          data-testid="daily-fees-table"
          empty={{ subtitle: t('redemptionFeeDetail.table.empty') }}
          enableSorting={false}
          error={error}
          loading={loading}
          onRowClick={onRowClick}
          pageSize={pageSize}
          retry={refetch}
          showHeader={false}
        />
      </Body>
    </TableCard>
  )
}
